// FeedbackPanel — "Give feedback" and "Report a problem" (pen I-06, I-08),
// pure.
//
// Problem / Idea / Question (Problem by default), the person's own words,
// "Sent with your report" (each chip shown exactly as sent, removable), "You
// may contact me about this", the privacy line and Send. Opened from an error
// box it is titled "Report a problem", says the error details are attached and
// the text is optional.
//
// Props-driven: the draft and the send status arrive as props; every change
// reports up. A send that failed keeps everything written and says why in the
// panel (pen: "Never lose what was written"); the person tries again with Send.
// Left out, by owner decision or because nothing backs them yet: the
// screenshot checkbox (no capture without a new dependency; the issue says no
// screenshots) and "You can follow your reports in Settings" (Settings "Your
// reports" is not built).

export type { FeedbackSendStatus };
export { FeedbackPanel };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { SendFailure } from "../../app/feedback.js";
import type {
    FeedbackContextItem,
    FeedbackKind,
} from "../../app/ports/feedback.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { SegmentedControl } from "../SegmentedControl/SegmentedControl.js";
import { Switch } from "../Switch/Switch.js";
import { Textarea } from "../Textarea/Textarea.js";
import "./Feedback.css";

// Where the last Send stands. `failed` names why, so the panel can say it.
type FeedbackSendStatus =
    | { readonly type: "idle" }
    | { readonly type: "sending" }
    | {
          readonly type: "failed";
          readonly reason: SendFailure;
      };

type FeedbackPanelProps = {
    // Opened from an error box: titled "Report a problem", text optional.
    readonly fromError: boolean;
    readonly kind: FeedbackKind;
    readonly onKind: (kind: FeedbackKind) => void;
    readonly text: string;
    readonly onText: (text: string) => void;
    readonly chips: readonly FeedbackContextItem[];
    readonly onRemoveChip: (chip: FeedbackContextItem["type"]) => void;
    readonly mayContact: boolean;
    readonly onMayContact: (may: boolean) => void;
    readonly canSend: boolean;
    readonly status: FeedbackSendStatus;
    readonly onSend: () => void;
    readonly onClose: () => void;
};

const isKind = (v: string): v is FeedbackKind =>
    v === "problem" || v === "idea" || v === "question";

const useChipLabel = (): ((chip: FeedbackContextItem) => string) => {
    const { t } = useLingui();
    return (chip) => {
        switch (chip.type) {
            case "feedback_context_view":
                return t`View: ${chip.view}`;
            case "feedback_context_client":
                return t`Version ${chip.version} · ${chip.browser} · ${chip.os}`;
            case "feedback_context_error":
                return chip.summary === ""
                    ? t`Error ${chip.errorId}`
                    : t`Error ${chip.errorId} · ${chip.summary}`;
            default:
                return exhaustSwitchCase(chip);
        }
    };
};

const useFailureText = (): ((reason: SendFailure) => string) => {
    const { t } = useLingui();
    return (reason) => {
        switch (reason) {
            case "too_long":
                return t`That's longer than a report can be. Shorten it and send again.`;
            case "empty":
                return t`Write a few words first.`;
            case "daily_limit":
                return t`You've sent as many reports as you can today. Your text is kept; send it tomorrow.`;
            case "offline":
                return t`Couldn't reach the server. Your text is kept; try again.`;
            case "other":
                return t`Couldn't send the report. Your text is kept; try again.`;
            default:
                return exhaustSwitchCase(reason);
        }
    };
};

const FeedbackPanel = ({
    fromError,
    kind,
    onKind,
    text,
    onText,
    chips,
    onRemoveChip,
    mayContact,
    onMayContact,
    canSend,
    status,
    onSend,
    onClose,
}: FeedbackPanelProps): ReactNode => {
    const { t } = useLingui();
    const chipLabel = useChipLabel();
    const failureText = useFailureText();
    const title = fromError ? t`Report a problem` : t`Give feedback`;
    const kinds = [
        {
            value: "problem",
            label: t`Problem`,
            icon: <Icon name="feedback-problem" size="sm" />,
        },
        {
            value: "idea",
            label: t`Idea`,
            icon: <Icon name="feedback-idea" size="sm" />,
        },
        {
            value: "question",
            label: t`Question`,
            icon: <Icon name="help" size="sm" />,
        },
    ];
    const sending = status.type === "sending";
    return (
        <form
            className="bilda-feedback"
            aria-label={title}
            onSubmit={(e) => {
                e.preventDefault();
                if (canSend && !sending) onSend();
            }}
        >
            <div className="bilda-feedback__head">
                <h2 className="bilda-feedback__title">{title}</h2>
                <IconButton
                    label={t`Close`}
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={onClose}
                >
                    <Icon name="close" />
                </IconButton>
            </div>
            {fromError ? (
                <p className="bilda-feedback__attached">
                    <Icon name="paperclip" size="sm" />
                    <span>{t`We've attached the error details. A sentence from you helps, but isn't required.`}</span>
                </p>
            ) : null}
            <SegmentedControl
                options={kinds}
                value={kind}
                onChange={(v) => {
                    if (isKind(v)) onKind(v);
                }}
                ariaLabel={t`Kind of feedback`}
            />
            <Textarea
                className="bilda-feedback__text"
                rows={4}
                value={text}
                aria-label={t`Your feedback`}
                placeholder={
                    fromError
                        ? t`Optional: what were you doing just before?`
                        : t`What happened? What did you expect?`
                }
                onChange={(e) => onText(e.target.value)}
            />
            {chips.length > 0 ? (
                <section className="bilda-feedback__context">
                    <h3 className="bilda-feedback__label">{t`Sent with your report`}</h3>
                    <ul className="bilda-feedback__chips">
                        {chips.map((chip) => {
                            const label = chipLabel(chip);
                            return (
                                <li
                                    key={chip.type}
                                    className="bilda-feedback__chip"
                                >
                                    <span className="bilda-feedback__chip-text">
                                        {label}
                                    </span>
                                    <IconButton
                                        label={t`Don't send: ${label}`}
                                        variant="ghost"
                                        size="sm"
                                        type="button"
                                        onClick={() => onRemoveChip(chip.type)}
                                    >
                                        <Icon name="close" size="xs" />
                                    </IconButton>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ) : null}
            <div className="bilda-feedback__contact">
                <span>{t`You may contact me about this`}</span>
                <Switch
                    checked={mayContact}
                    onCheckedChange={onMayContact}
                    label={t`You may contact me about this`}
                />
            </div>
            <p className="bilda-feedback__privacy">
                <Icon name="shield" size="sm" />
                <span>{t`Please don't write anything about individual students.`}</span>
            </p>
            {status.type === "failed" ? (
                <p className="bilda-feedback__failure" role="alert">
                    {failureText(status.reason)}
                </p>
            ) : null}
            <div className="bilda-feedback__foot">
                <Button
                    type="submit"
                    variant="primary"
                    disabled={!canSend || sending}
                    aria-busy={sending || undefined}
                >
                    {sending ? t`Sending…` : t`Send`}
                </Button>
            </div>
        </form>
    );
};
